(() => {
  if (customElements.get('veakoo-skin-questionnaire')) return;

  const choices = [
    { dry: 'Dry or tight', 'oil-rich': 'Oily or shiny', balanced: 'Generally comfortable' },
    { sensitive: 'Comfort for easily irritated skin', blemishes: 'Recurring blemishes', everyday: 'Everyday comfort' },
    { one: 'One starting product', three: 'Up to three products' }
  ];

  class QuizState {
    constructor() { this.reset(); }
    reset() { this.answers = [null, null, null]; this.index = 0; }
    select(value) {
      if (!choices[this.index] || !Object.hasOwn(choices[this.index], value)) return false;
      this.answers[this.index] = value;
      return true;
    }
    next() {
      if (!choices[this.index] || !Object.hasOwn(choices[this.index], this.answers[this.index])) return false;
      this.index += 1;
      return true;
    }
    back() { this.index = Math.max(0, this.index - 1); }
    result() {
      if (!this.answers.every((v, i) => Object.hasOwn(choices[i], v))) return null;
      const [feel, priority, routine] = this.answers;
      return { category: priority === 'sensitive' ? 'sensitive' : priority === 'blemishes' ? 'acne-prone' : feel, count: routine === 'one' ? 1 : 3 };
    }
  }

  class SkinQuestionnaire extends HTMLElement {
    static State = QuizState;
    static photoError(file) {
      if (!file) return 'Please choose an image, or skip the photo.';
      if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) return 'Please choose a JPG, PNG or WebP image. You can also skip the photo.';
      if (!file.size || file.size > 10 * 1024 * 1024) return 'Please choose an image between 1 byte and 10 MB, or skip the photo.';
      return '';
    }
    constructor() {
      super();
      this.state = new QuizState();
      this.photoVersion = 0;
      this.photoURL = null;
    }
    connectedCallback() {
      if (this.events) return;
      this.events = new AbortController();
      const options = { signal: this.events.signal };
      this.photo = this.querySelector('[data-photo]');
      this.photoActions = this.querySelector('[data-photo-actions]');
      this.status = this.querySelector('[data-status]');
      this.inputs = Array.from(this.querySelectorAll('input[type="file"]'));
      this.querySelector('[data-enhanced]').hidden = false;
      this.querySelector('[data-fallback]').hidden = true;
      this.addEventListener('change', event => {
        if (event.target.matches('input[type="file"]')) {
          if (event.target.files[0]) this.choosePhoto(event.target.files[0]);
        }
        if (event.target.matches('input[type="radio"]')) {
          this.state.select(event.target.value);
          this.status.textContent = '';
        }
      }, options);
      this.addEventListener('submit', event => {
        const form = event.target.closest('[data-question]');
        if (!form) return;
        event.preventDefault();
        if (Number(form.dataset.question) !== this.state.index) return;
        const selected = form.querySelector('input:checked');
        if (!selected || !this.state.select(selected.value) || !this.state.next()) {
          this.status.textContent = 'Choose an answer to continue.';
          form.reportValidity();
          return;
        }
        if (this.state.index === choices.length) this.renderResult();
        else this.show(`question-${this.state.index}`);
      }, options);
      this.addEventListener('click', event => {
        const button = event.target.closest('[data-action]');
        if (!button) return;
        switch (button.dataset.action) {
          case 'start': this.state.index = 0; this.show('question-0'); break;
          case 'skip': this.clearPhoto(); this.state.index = 0; this.show('question-0'); break;
          case 'remove': this.clearPhoto(); this.status.textContent = 'Photo removed from this preview.'; break;
          case 'back':
            if (this.state.index === 0) this.show('intro');
            else { this.state.back(); this.show(`question-${this.state.index}`); }
            break;
          case 'edit': this.state.back(); this.show(`question-${this.state.index}`); break;
          case 'restart': this.restart(); break;
        }
      }, options);
      this.restart(false);
    }
    disconnectedCallback() {
      this.events?.abort();
      this.events = null;
      this.clearPhoto();
      this.state.reset();
    }
    clearPhoto() {
      this.photoVersion += 1;
      if (this.photoURL) URL.revokeObjectURL(this.photoURL);
      this.photoURL = null;
      if (this.photo) { this.photo.hidden = true; this.photo.removeAttribute('src'); }
      if (this.photoActions) this.photoActions.hidden = true;
      this.inputs?.forEach(input => { input.value = ''; });
    }
    async choosePhoto(file) {
      this.clearPhoto();
      const error = SkinQuestionnaire.photoError(file);
      if (error) { this.status.textContent = error; return; }
      const version = this.photoVersion;
      let url;
      try {
        url = URL.createObjectURL(file);
        this.photoURL = url;
        this.status.textContent = 'Opening your local preview…';
        await new Promise((resolve, reject) => {
          const image = new Image();
          image.onload = resolve;
          image.onerror = reject;
          image.src = url;
        });
        if (version !== this.photoVersion) return;
        this.photo.src = url;
        this.photo.hidden = false;
        this.photoActions.hidden = false;
        this.status.textContent = 'Preview ready. Your photo is not uploaded or analyzed.';
      } catch {
        if (version !== this.photoVersion) return;
        this.clearPhoto();
        this.status.textContent = 'We could not open this image. Try a JPG, PNG or WebP image, or skip the photo.';
      }
    }
    show(name, focus = true) {
      this.querySelectorAll('[data-screen]').forEach(screen => { screen.hidden = screen.dataset.screen !== name; });
      this.status.textContent = '';
      const progress = this.querySelector('[data-progress]');
      progress.hidden = !name.startsWith('question-');
      if (!progress.hidden) progress.textContent = `Question ${this.state.index + 1} of ${choices.length}`;
      if (focus) this.querySelector(`[data-screen="${name}"] [data-focus]`)?.focus({ preventScroll: false });
    }
    renderResult() {
      const result = this.state.result();
      if (!result) return;
      let matched = false;
      this.querySelectorAll('[data-result]').forEach(block => {
        const active = !matched && block.dataset.result === result.category;
        block.hidden = !active;
        block.dataset.count = String(result.count);
        if (active) matched = true;
        block.querySelectorAll('[data-product]').forEach((card, index) => { card.hidden = index >= result.count; });
      });
      this.querySelector('[data-missing]').hidden = matched;
      this.querySelector('[data-summary]').textContent = this.state.answers.map((value, index) => choices[index][value]).join(' · ');
      this.show('results');
    }
    restart(focus = true) {
      this.state.reset();
      this.clearPhoto();
      this.querySelectorAll?.('input[type="radio"]').forEach(input => { input.checked = false; });
      this.show('intro', focus);
    }
  }
  customElements.define('veakoo-skin-questionnaire', SkinQuestionnaire);
})();
